import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { ArrowRight, Loader2, ShieldCheck, Eye, EyeOff, MailCheck } from "lucide-react";

export default function Login() {
  const { user, login, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mfa, setMfa] = useState(null); // {challenge, email_hint}
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(true);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(email.trim(), password);
      if (res?.mfa_required) { setMfa(res); setCode(""); return; }
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e?.preventDefault();
    if (code.length !== 6) return;
    setLoading(true);
    try {
      await verifyMfa(mfa.challenge, code, remember);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setCode("");
      if (err.response?.status === 400 || err.response?.status === 429) setMfa(null);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try { const { data } = await api.post("/auth/mfa/resend", { challenge: mfa.challenge }); setMfa(data); toast.success("A new verification email is on its way"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between bg-black p-12 overflow-hidden">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1567360425618-1594206637d2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(229,25,33,0.55), rgba(0,0,0,0.85))" }} />
        <div className="relative z-10">
          <BrandLogo light size="lg" />
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-display text-4xl xl:text-5xl font-black text-white tracking-tighter leading-none">
            Integrated Business Portal
          </h1>
          <p className="mt-5 text-white/70 text-base leading-relaxed">
            Procurement, tender quotations, inventory, gas exchange POS and reporting — unified in one secure command centre.
          </p>
          <div className="mt-8 flex items-center gap-2 text-white/60 text-sm">
            <ShieldCheck className="h-4 w-4" /> Role-based access &bull; Audit trails &bull; Encrypted
          </div>
        </div>
        <div className="relative z-10 text-white/40 text-xs">© {new Date().getFullYear()} Bocally Holdings</div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm fade-up">
          <div className="lg:hidden mb-8"><BrandLogo size="lg" /></div>
          {mfa ? (
            <form onSubmit={verify} data-testid="mfa-step">
              <p className="text-[11px] uppercase tracking-[0.25em] text-primary font-semibold">Two-step verification</p>
              <h2 className="font-display text-3xl font-bold tracking-tight mt-2">Check your email</h2>
              <p className="text-muted-foreground text-sm mt-2">We sent a 6-digit verification to <span className="font-medium text-foreground" data-testid="mfa-email-hint">{mfa.email_hint}</span>. It expires in 10 minutes.</p>
              <div className="mt-8 flex justify-center">
                <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="mfa-code-input" autoFocus>
                  <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-11 text-lg" />)}</InputOTPGroup>
                </InputOTP>
              </div>
              <label className="mt-6 flex items-center gap-2 text-sm text-muted-foreground"><input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="mfa-remember-checkbox" /> Trust this device for 30 days</label>
              <Button type="submit" disabled={loading || code.length !== 6} data-testid="mfa-verify-button" className="mt-5 w-full h-11 bg-primary hover:bg-[#C81219] text-white font-semibold">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><MailCheck className="mr-2 h-4 w-4" /> Verify &amp; Sign In</>}
              </Button>
              <div className="mt-5 flex justify-between text-sm">
                <button type="button" onClick={() => setMfa(null)} className="text-muted-foreground hover:underline" data-testid="mfa-back-button">Back</button>
                <button type="button" onClick={resend} className="text-primary font-medium hover:underline" data-testid="mfa-resend-button">Resend email</button>
              </div>
            </form>
          ) : (<>
          <p className="text-[11px] uppercase tracking-[0.25em] text-primary font-semibold">Secure Sign In</p>
          <h2 className="font-display text-3xl font-bold tracking-tight mt-2">Access your portal</h2>
          <p className="text-muted-foreground text-sm mt-2">Enter your credentials to continue.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">Email address</Label>
              <Input
                id="email" type="email" required data-testid="login-email-input"
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@bolletechnologies.co.za"
                className="h-11 focus-visible:ring-primary/20 focus-visible:border-primary"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">Password</Label>
              <div className="relative">
                <Input
                  id="password" type={showPw ? "text" : "password"} required data-testid="login-password-input"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-11 pr-11 focus-visible:ring-primary/20 focus-visible:border-primary"
                />
                <button
                  type="button" onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Hide password" : "Show password"} data-testid="toggle-password-visibility"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button
              type="submit" disabled={loading} data-testid="login-submit-button"
              className="w-full h-11 bg-primary hover:bg-[#C81219] text-white font-semibold group transition-colors"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign In <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-0.5 transition-transform" /></>}
            </Button>
          </form>
          <div className="mt-5 text-center">
            <Link to="/forgot-password" className="text-sm text-primary hover:underline font-medium" data-testid="forgot-password-link">
              Forgot your password?
            </Link>
          </div>
          </>)}
        </div>
      </div>
    </div>
  );
}
